import * as React from "react";
import { Paperclip, Download, Trash2, Loader2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi } from "@/lib/api";
import {
  useTaskAttachments,
  useUploadAttachment,
  useDeleteAttachment,
} from "./use-tasks";

const MAX_BYTES = 10 * 1024 * 1024; // 10 MB

function formatSize(bytes: number | null | undefined): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Two modes:
 *   - Saved:    pass `taskId`. Uploads/deletes hit the API immediately.
 *   - Pending:  pass `pendingFiles` + `onPendingFilesChange`. Files stay in
 *               parent state until the parent uploads them after creating
 *               the task.
 */
export function AttachmentsManager({
  taskId,
  pendingFiles,
  onPendingFilesChange,
}: {
  taskId?: number | string;
  pendingFiles?: File[];
  onPendingFilesChange?: (files: File[]) => void;
}) {
  const api = useApi();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const attachmentsQ = useTaskAttachments(taskId);
  const upload = useUploadAttachment();
  const del = useDeleteAttachment();
  const [error, setError] = React.useState<string | null>(null);

  const isSaved =
    taskId !== undefined && taskId !== null && taskId !== "";
  const pending = pendingFiles ?? [];

  async function handleFiles(files: FileList | null) {
    if (!files) return;
    setError(null);
    const valid: File[] = [];
    for (const file of Array.from(files)) {
      if (file.size > MAX_BYTES) {
        setError(`"${file.name}" is too large (max 10 MB).`);
        continue;
      }
      valid.push(file);
    }
    if (valid.length === 0) {
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    if (isSaved) {
      for (const file of valid) {
        try {
          await upload.mutateAsync({ taskId: taskId as number | string, file });
        } catch (e) {
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    } else if (onPendingFilesChange) {
      onPendingFilesChange([...pending, ...valid]);
    }
    if (inputRef.current) inputRef.current.value = "";
  }

  async function handleDownload(
    attachmentId: number | string,
    fileName: string,
  ) {
    setError(null);
    try {
      const res = await api(`/api/attachments/${attachmentId}/download`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function handleDelete(attachmentId: number | string, fileName: string) {
    if (!window.confirm(`Delete "${fileName}"?`)) return;
    del.mutate(attachmentId);
  }

  function removePending(idx: number) {
    if (!onPendingFilesChange) return;
    onPendingFilesChange(pending.filter((_, i) => i !== idx));
  }

  const saved = attachmentsQ.data ?? [];
  const showLoading = isSaved && attachmentsQ.isLoading;
  const showEmpty =
    !showLoading && (isSaved ? saved.length === 0 : pending.length === 0);

  return (
    <div className="flex flex-col gap-2">
      <input
        ref={inputRef}
        type="file"
        multiple
        onChange={(e) => handleFiles(e.target.files)}
        className="hidden"
      />

      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={upload.isPending}
        >
          {upload.isPending ? (
            <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
          ) : (
            <Upload className="w-3.5 h-3.5 mr-2" />
          )}
          {upload.isPending ? "Uploading…" : "Add file"}
        </Button>
        <span className="text-xs text-muted-foreground">
          Max 10 MB per file
          {!isSaved && pending.length > 0
            ? " · uploads after the task is created"
            : ""}
        </span>
      </div>

      {showLoading ? (
        <p className="text-xs text-muted-foreground">Loading attachments…</p>
      ) : showEmpty ? (
        <p className="text-xs text-muted-foreground">
          {isSaved ? "No attachments yet." : "No files attached yet."}
        </p>
      ) : isSaved ? (
        <ul className="flex flex-col gap-1">
          {saved.map((a) => (
            <li
              key={String(a.id)}
              className="flex items-center gap-2 text-sm bg-muted/30 rounded-md px-2 py-1.5"
            >
              <Paperclip className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              <span className="flex-1 min-w-0 truncate" title={a.file_name}>
                {a.file_name}
              </span>
              <span className="text-xs text-muted-foreground flex-shrink-0">
                {formatSize(a.size_bytes)}
              </span>
              <button
                type="button"
                aria-label={`Download ${a.file_name}`}
                title="Download"
                onClick={() => handleDownload(a.id, a.file_name)}
                className="text-muted-foreground hover:text-foreground p-1 rounded transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                aria-label={`Delete ${a.file_name}`}
                title="Delete"
                onClick={() => handleDelete(a.id, a.file_name)}
                disabled={del.isPending}
                className="text-muted-foreground/60 hover:text-red-600 hover:bg-red-50 p-1 rounded transition-colors disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <ul className="flex flex-col gap-1">
          {pending.map((file, idx) => (
            <li
              key={`pending-${idx}-${file.name}`}
              className="flex items-center gap-2 text-sm bg-muted/30 rounded-md px-2 py-1.5"
            >
              <Paperclip className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              <span className="flex-1 min-w-0 truncate" title={file.name}>
                {file.name}
              </span>
              <span className="text-xs text-muted-foreground flex-shrink-0">
                {formatSize(file.size)}
              </span>
              <button
                type="button"
                aria-label={`Remove ${file.name}`}
                title="Remove"
                onClick={() => removePending(idx)}
                className="text-muted-foreground/60 hover:text-red-600 hover:bg-red-50 p-1 rounded transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
}
